import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '@core/environment/environment.service';
import {Router} from '@angular/router';
import {TokenStorageService} from '@core/modules/rest/authentication/token-storage.service';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {BayerPopupComponent} from './popups/bayer-popup/bayer-popup.component';
import {HealthbankPopupComponent} from './popups/healthbank-popup/healthbank-popup.component';
import {TranslateService} from '@ngx-translate/core';
import {getLanguageCodeByLanguageEnum, getLanguageEnumByLanguageCode, LanguageEnum} from '@core/enums/language.enum';
import {Store} from '@ngrx/store';
import {AppState} from '@core/reducers';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {ReadingSpeedEnum} from '@core/enums/reading-speed.enum';
import {WarningTwoOptionDialogModel} from '../../secured/views/modules/welcome-page/components/popups/start-new-consent-popup/model/warning-two-option-dialog.model';
import {StartNewConsentPopupComponent} from '../../secured/views/modules/welcome-page/components/popups/start-new-consent-popup/start-new-consent-popup.component';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {PubliclinkRestService} from '@core/modules/rest/publiclink/publiclink-rest.service';

@UntilDestroy()
@Component({
    selector: 'app-welcome',
    templateUrl: './welcome.component.html',
    styleUrls: ['./welcome.component.scss']
})
export class WelcomeComponent implements OnInit, OnDestroy {
    roles: string[] = [];
    dialogConfig = new MatDialogConfig();
    englishButtonEnum = LanguageEnum.ENGLISH_GB;
    germanButtonEnum = LanguageEnum.GERMAN;
    serbianButtonEnum = LanguageEnum.SERBIAN;
    activeLanguage: LanguageEnum | undefined;

    audio = new Audio();
    ttsUrl: string | undefined;
    firstPlay = true;
    ttsIconState = TtsControlEnum.playState;
    autoTextRead = false;
    playState = TtsControlEnum.playState;
    repeatButtonDisabled = true;

    constructor(private http: HttpClient, private environmentService: EnvironmentService, private router: Router,
                private tokenStorage: TokenStorageService, private dialog: MatDialog, private translate: TranslateService,
                private store: Store<AppState>, private publicService: PubliclinkRestService) {
        history.pushState(null, '', window.location.href);
    }

    ngOnInit(): void {
        this.activeLanguage = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')!);
        this.audio.addEventListener('ended', this.setButtonOnEnd, false);
        this.autoTextRead = sessionStorage.getItem('audioMode') === 'true';
        if (this.autoTextRead) {
            this.openAudioPopUp();
        }
    }

    setButtonOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    popupConfig(currentPopup: any): MatDialogRef<any> {
        this.dialogConfig.autoFocus = false;
        this.dialogConfig.disableClose = false;
        this.dialogConfig.restoreFocus = true;
        return this.dialog.open(currentPopup, this.dialogConfig);
    }

    openBayerPopUpDialog(): void {
        const dialogRef = this.popupConfig(BayerPopupComponent);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
        dialogRef.afterClosed().subscribe(() => {
            this.dialogConfig.restoreFocus = true;
            const parent = document.getElementById('bayerFocus');
            parent.querySelector('button').focus();
        });
    }

    openHealthBankPopUpDialog(): void {
        const dialogRef = this.popupConfig(HealthbankPopupComponent);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
        dialogRef.afterClosed().subscribe(() => {
            this.dialogConfig.restoreFocus = true;
            const parent = document.getElementById('healthbankFocus');
            parent.querySelector('button').focus();
        });
    }

    changeLanguage(language: LanguageEnum): void {
        if (this.activeLanguage !== language) {
            this.pause(false);
            this.firstPlay = true;
            this.repeatButtonDisabled = true;
            const languageCode = getLanguageCodeByLanguageEnum(language);
            this.translate.use(languageCode);
            localStorage.setItem('languageCode', languageCode);
            this.activeLanguage = language;
        }
    }

    readText(): void {
        const textToRead = this.translate.instant('VOICE_OUTPUT_TEXTS.LANDING_PAGE');
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech(textToRead);
        } else {
            if (this.audio.ended) {
                this.play();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resume(false);
                } else {
                    this.pause(false);
                }
            }
        }
    }

    play(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    resume(automatic: boolean): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState && automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
            if (this.ttsIconState === TtsControlEnum.playState && !automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    pause(automatic: boolean): void {
        if (!automatic) {
            this.ttsIconState = TtsControlEnum.playState;
        }
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    convertTextToSpeech(textToRead: string): void {
        const pollyObject = {
            text: textToRead,
            readingSpeed: ReadingSpeedEnum.MEDIUM,
            readingLanguage: this.activeLanguage,
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonDisabled = false;
        this.publicService.getPublicTTS(pollyObject).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.play();
            }
        });
    }

    openAudioPopUp(): void {
        const dialogData: WarningTwoOptionDialogModel = {
            title: this.translate.instant('VOICE_OUTPUT_TEXTS.AUDIO_POPUP.TITLE'),
            content: this.translate.instant('VOICE_OUTPUT_TEXTS.AUDIO_POPUP.DESCRIPTION'),
            icon: 'volume_up',
            iconColor: 'info',
            leftButtonText: this.translate.instant('GENERAL.CANCEL'),
            rightButtonText: this.translate.instant('GENERAL.START')
        };
        const dialogRef = this.openConfirmationPopup(dialogData);
        dialogRef.componentInstance.closeDialogEvent.subscribe(() => {
            dialogRef.close();
            this.autoTextRead = false;
            sessionStorage.removeItem('audioMode');
        });
        dialogRef.componentInstance.confirmActionEvent.subscribe(() => {
            dialogRef.close();
            this.readText();
        });
    }

    openConfirmationPopup(dialogInfo: WarningTwoOptionDialogModel): MatDialogRef<StartNewConsentPopupComponent> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = dialogInfo;
        dialogConfig.autoFocus = true;
        dialogConfig.disableClose = true;
        return this.dialog.open(StartNewConsentPopupComponent, dialogConfig);
    }

    ngOnDestroy(): void {
        this.pause(false);
    }
}
